import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAdminStore from './store';
import {
  Users,
  UserPlus,
  Shield,
  ShieldCheck,
  Key,
  Settings,
  Activity,
  AlertCircle,
  TrendingUp,
  Clock,
  Eye,
  EyeOff,
  Zap,
  Database,
  RotateCcw
} from 'lucide-react';

const AdminIAMDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDevTools, setShowDevTools] = useState(false);
  
  const {
    users,
    groups,
    permissions,
    loading,
    errors,
    currentUser,
    latency,
    persistData,
    setCurrentUserRole,
    setLatency,
    setPersistData,
    resetData
  } = useAdminStore();

  // Stats calculations
  const stats = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.status === 'active').length,
    totalGroups: groups.length,
    totalPermissions: permissions.length,
    recentActivity: Math.floor(Math.random() * 47) + 3 // Mock recent activity count
  };

  const quickActions = [
    {
      title: 'Create User',
      description: 'Add a new user to the system',
      icon: UserPlus,
      color: 'bg-blue-500',
      action: () => navigate('/admin-iam/users/new')
    },
    {
      title: 'Manage Groups',
      description: 'Configure user groups and permissions',
      icon: Shield,
      color: 'bg-green-500',
      action: () => navigate('/admin-iam/groups')
    },
    {
      title: 'View Permissions',
      description: 'Review system permissions',
      icon: Key,
      color: 'bg-purple-500',
      action: () => navigate('/admin-iam/permissions')
    },
    {
      title: 'Security Settings',
      description: 'Configure system security options',
      icon: Settings,
      color: 'bg-orange-500',
      action: () => navigate('/admin-iam/settings')
    }
  ];

  const recentActivity = [
    {
      id: 1,
      action: 'User created',
      user: 'Dr. Sarah Johnson',
      timestamp: '2 minutes ago',
      type: 'create'
    },
    {
      id: 2,
      action: 'Group updated',
      user: 'Doctors Group',
      timestamp: '15 minutes ago',
      type: 'update'
    },
    {
      id: 3,
      action: 'Permission granted',
      user: 'John Smith',
      timestamp: '1 hour ago',
      type: 'permission'
    },
    {
      id: 4,
      action: 'User deactivated',
      user: 'temp-user@example.com',
      timestamp: '2 hours ago',
      type: 'deactivate'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'create':
        return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'update':
        return <Settings className="w-4 h-4 text-blue-500" />;
      case 'permission':
        return <Key className="w-4 h-4 text-purple-500" />;
      case 'deactivate':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  // Development Tools Component
  const DevTools = () => (
    <div className="fixed bottom-4 right-4 bg-gray-800 text-white p-4 rounded-lg shadow-xl z-50 max-w-xs">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-sm">Dev Tools</h4>
        <button 
          onClick={() => setShowDevTools(false)}
          className="text-gray-400 hover:text-white"
        >
          <EyeOff className="w-4 h-4" />
        </button>
      </div>
      
      <div className="space-y-3 text-xs">
        <div>
          <label className="block text-gray-300 mb-1">Mock User Role:</label>
          <select 
            value={currentUser.role}
            onChange={(e) => setCurrentUserRole(e.target.value)}
            className="w-full px-2 py-1 bg-gray-700 rounded text-white text-xs"
          >
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        
        <div>
          <label className="block text-gray-300 mb-1">API Latency: {latency}ms</label>
          <input 
            type="range"
            min="0"
            max="2000"
            value={latency}
            onChange={(e) => setLatency(Number(e.target.value))}
            className="w-full"
          />
        </div>
        
        <div>
          <label className="flex items-center space-x-2">
            <input 
              type="checkbox"
              checked={persistData}
              onChange={(e) => setPersistData(e.target.checked)}
              className="rounded"
            />
            <span className="text-gray-300">Persist Data</span>
          </label>
        </div>
        
        <button 
          onClick={resetData}
          className="w-full px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs flex items-center justify-center space-x-1"
        >
          <RotateCcw className="w-3 h-3" />
          <span>Reset Data</span>
        </button>
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* <Sidebar /> */}
      <div className="flex-1">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <Shield className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    Identity & Access Management
                  </h1>
                  <p className="text-sm text-gray-500">
                    Manage users, groups, and permissions
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {currentUser.displayName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {currentUser.role}
                  </p>
                </div>
                
                <button
                  onClick={() => setShowDevTools(!showDevTools)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title="Toggle Dev Tools"
                >
                  <Zap className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.totalUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Users</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.activeUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">User Groups</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.totalGroups}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Key className="w-8 h-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Permissions</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.totalPermissions}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Activity className="w-8 h-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Recent Activity</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.recentActivity}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickActions.map((action, index) => (
                <div
                  key={index}
                  onClick={action.action}
                  className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                >
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 p-3 rounded-lg ${action.color}`}>
                      <action.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-900">
                        {action.title}
                      </h3>
                      <p className="text-xs text-gray-600 mt-1">
                        {action.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Recent Activity</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-center">
                      <div className="flex-shrink-0">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {activity.action}
                        </p>
                        <p className="text-sm text-gray-600">
                          {activity.user}
                        </p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-xs text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {activity.timestamp}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">System Status</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Database className="w-5 h-5 text-green-500 mr-2" />
                      <span className="text-sm text-gray-900">Database</span>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Healthy
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ShieldCheck className="w-5 h-5 text-green-500 mr-2" />
                      <span className="text-sm text-gray-900">Authentication</span>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Activity className="w-5 h-5 text-yellow-500 mr-2" />
                      <span className="text-sm text-gray-900">Background Jobs</span>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Processing
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Zap className="w-5 h-5 text-blue-500 mr-2" />
                      <span className="text-sm text-gray-900">API Performance</span>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {latency}ms avg
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Development Tools */}
        {showDevTools && <DevTools />}
      </div>
    </div>
  );
};

export default AdminIAMDashboard;
